import React, { useState, useRef, useEffect } from "react";
import "../../css/designSystem/Navbar.css";
import logo from "../../assets/icons/navbar_logo.svg";
import { useLocation, useNavigate } from "react-router-dom";
import PrimaryButton from "./buttons/PrimaryButton";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMortgageDropdown, setShowMortgageDropdown] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [showCalculatorsDropdown, setShowCalculatorsDropdown] = useState(false);
  const mortgageRef = useRef(null);
  const servicesRef = useRef(null);
  const toolsRef = useRef(null);
  const calculatorsRef = useRef(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (mortgageRef.current && !mortgageRef.current.contains(event.target)) {
        setShowMortgageDropdown(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setShowServicesDropdown(false);
      }
      if (toolsRef.current && !toolsRef.current.contains(event.target)) {
        setShowToolsDropdown(false);
      }
      if (
        calculatorsRef.current &&
        !calculatorsRef.current.contains(event.target)
      ) {
        setShowCalculatorsDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!location.hash) return;

    const sectionId = location.hash.slice(1);
    window.setTimeout(() => {
      document
        .getElementById(sectionId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [location.pathname, location.hash]);

  const servicesList = [
    { name: "Residential Mortgages", path: "/residential-mortgages" },
    { name: "Buy-to-Let Mortgages", path: "/buy-to-let-mortgages" },
    { name: "Specialist Lending", path: "/specialist-lending" },
    { name: "Protection", path: "/protection" },
    { name: "Other Services", path: "/other-services" },
  ];
  const calculatorsList = [
    {
      name: "Borrowing Amount Calculator",
      path: "/calculators/mortgage-borrow",
    },
    {
      name: "Repayment Calculator",
      path: "/calculators/mortgage-repayment",
    },
    { name: "Stamp Duty Calculator", path: "/calculators/stamp-duty" },
  ];



  const handleNavigate = (path) => {
    setShowMortgageDropdown(false);
    setShowServicesDropdown(false);
    setShowToolsDropdown(false);
    setShowCalculatorsDropdown(false);
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  const handleSectionNavigate = ({ path, sectionId }) => {
    const nextPath = sectionId ? `${path}#${sectionId}` : path;
    handleNavigate(nextPath);

    if (location.pathname === path && sectionId) {
      window.setTimeout(() => {
        document
          .getElementById(sectionId)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  };

  const handleContactClick = () => {
    setShowMortgageDropdown(false);
    setShowServicesDropdown(false);
    setShowToolsDropdown(false);
    setShowCalculatorsDropdown(false);
    setIsMobileMenuOpen(false);
    document
      .getElementById("contact-us")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navTextClass = (path, exact = true) => {
    const isActive = exact
      ? location.pathname === path
      : location.pathname.startsWith(path);

    return `nav-text ${isActive ? "is-active" : ""}`;
  };

  const servicesNavTextClass = () => {
    const isActive = servicesList.some(
      (item) => location.pathname === item.path,
    );

    return `nav-text ${isActive ? "is-active" : ""}`;
  };

  const calculatorsNavTextClass = () => {
    const isActive =
      location.pathname === "/mortgage-calculators" ||
      calculatorsList.some((item) => location.pathname === item.path);

    return `nav-text ${isActive ? "is-active" : ""}`;
  };

  const dropdownItemClass = (path) => {
    return `navbar-dropdown-item content ${
      location.pathname === path ? "is-active" : ""
    }`;
  };

  const sectionDropdownItemClass = ({ path, sectionId }) => {
    const isActive =
      location.pathname === path &&
      (!sectionId || location.hash === `#${sectionId}`);

    return `navbar-dropdown-item content ${isActive ? "is-active" : ""}`;
  };

  return (
    <nav className="navbar">
      <div className="navbar-main-row">
        <div className="navbar-left">
          <img
            src={logo}
            alt="CAPC Mortgages"
            className="navbar-logo"
            onClick={() => {
              handleNavigate("/");
            }}
          />
        </div>

        <button
          type="button"
          className={`navbar-toggle ${isMobileMenuOpen ? "is-open" : ""}`}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
          onClick={() => {
            setIsMobileMenuOpen((prev) => !prev);
            setShowMortgageDropdown(false);
            setShowServicesDropdown(false);
            setShowToolsDropdown(false);
            setShowCalculatorsDropdown(false);
          }}
        >
          <span className="navbar-toggle-line line-1" />
          <span className="navbar-toggle-line line-2" />
          <span className="navbar-toggle-line line-3" />
        </button>
      </div>

      <div className={`navbar-content ${isMobileMenuOpen ? "is-open" : ""}`}>
        <ul className="navbar-links">
          <li ref={servicesRef} style={{ position: "relative" }}>
            <span
              className={`${servicesNavTextClass()} has-dropdown`}
              aria-haspopup="true"
              aria-expanded={showServicesDropdown}
              onClick={() => {
                setShowServicesDropdown((previous) => !previous);
                setShowMortgageDropdown(false);
                setShowToolsDropdown(false);
                setShowCalculatorsDropdown(false);
              }}
            >
              our services
            </span>
            {showServicesDropdown && (
              <ul className="navbar-dropdown">
                {servicesList.map((item) => (
                  <li
                    key={item.sectionId || item.name}
                    className={sectionDropdownItemClass(item)}
                    onClick={() => handleSectionNavigate(item)}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li ref={toolsRef} style={{ position: "relative" }}>
            <span
              className={`${calculatorsNavTextClass()} has-dropdown`}
              aria-haspopup="true"
              aria-expanded={showToolsDropdown}
              onClick={() => {
                setShowToolsDropdown((previous) => !previous);
                setShowServicesDropdown(false);
                setShowMortgageDropdown(false);
                setShowCalculatorsDropdown(false);
              }}
              style={{ userSelect: "none" }}
            >
              calculators
            </span>
            {showToolsDropdown && (
              <ul className="navbar-dropdown">
                {calculatorsList.map((item) => (
                  <li
                    key={item.path}
                    className={dropdownItemClass(item.path)}
                    onClick={() => handleNavigate(item.path)}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li ref={mortgageRef} style={{ position: "relative" }}>
            <span
              className={navTextClass("/about-us")}
              onClick={() => handleNavigate("/about-us")}
            >
              About Us
            </span>
          </li>
        </ul>

        <div className="navbar-actions gap-m row-center">
          <PrimaryButton onClick={handleContactClick} text="Contact Us" />
        </div>

      </div>
    </nav>
  );
};

export default Navbar;
